import { MultiStepFormConfig, BaseFormData } from './components/types';
import { CriteriaStep, AbnLookupStep, BusinessDetailsStep, ContactDetailsStep, TrustDetailsStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  criteriaStepInitialData, 
  abnLookupStepInitialData, 
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData,
  combineStepData 
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({
  suitabilityCriteria: [
    "An ABN registered in Australia",
    "A business email address linked to your trading name",
    "A recent business bank statement (e.g. front page)",
    "A valid driver’s licence or passport"
  ],
});

export const formValidations = createFormValidations({
  businessDetailsStep: (data: BaseFormData) => {
    const hasBasicFields = !!(data.countryOfRegistration && data.businessType && data.industry);

    const industryValid = data.industry === "Other" 
      ? !!(data.customIndustry && typeof data.customIndustry === 'string' && data.customIndustry.trim().length > 0)
      : !!(data.industry && data.industry !== 'Select');

    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');
    
    return hasBasicFields && industryValid && stateValid;
  },
  trustDetailsStep: (data: BaseFormData) => {
    // Validation for trust details - only required if business type is Trust
    if (data.businessType !== 'Trust') return true;
    
    // Basic required fields
    const hasBasicFields = !!(data.trusteeType && data.trusteeName);
    
    // ABN/ACN only required if trustee is Corporate Entity
    const needsAbn = data.trusteeType === 'Corporate Entity';
    const hasAbn = needsAbn ? !!data.trustAbnAcn : true;
    
    return hasBasicFields && hasAbn;
  },
});

// Combine initial data from selected steps
const initialFormData = combineStepData(
  criteriaStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData
  // Add more step data here as you add more steps
);

export const octetPayEnquireFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'octet-pay-enquire',
  formOptions: formOptions,
  helperText: {
    firstStep: 'Takes under 2 minutes.',
    lastStep: 'Our team will get back to you within 1 business day'
  },
  steps: [
    {
      id: 'suitability-check',
      title: 'Quick suitability check.',
      description: 'Let\’s make sure you have everything ready to get started with <strong>OctetPay</strong>.',
      criteriaQuestion: "You\’ll just need the following:",
      infoBoxText: "You won\’t need to upload your documents just yet but our team will ask for them later in the process.",
      component: CriteriaStep,
      validation: () => true, // No validation needed for this step
    },
    {
      id: 'business-search',
      title: 'Tell us about your business.',
      description: 'Start typing your ABN, ACN, or business name. We\’ll fill in the rest.',
      validation: formValidations.abnLookupStep,
      component: AbnLookupStep,
    },
    {
      id: 'business-details',
      title: 'Confirm your business details.',
      description: 'These details help us configure OctetPay correctly, so you can make payments smoothly and start earning rewards.',
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessType: true,
        showIndustry: true,
      },
      validation: formValidations.businessDetailsStep,
      component: BusinessDetailsStep,

    },
    {
      id: 'trust-details',
      title: 'Tell us more about your trust.',
      description: 'We ask this because finance contracts are held by the trustee on behalf of the trust.',
      component: TrustDetailsStep,
      stepConfig: {
        showTrustType: false,
        showTrusteeType: true,
        showAbnAcn: true,
        showTrusteeName: true,
      },
      validation: formValidations.trustDetailsStep,
      shouldShow: (data: BaseFormData) => data.businessType === 'Trust',
    },
    {
      id: 'contact-details',
      title: 'Your contact details.',
      description: '<strong>How can we reach you?</strong> We\’ll get in touch within 1 working day to guide you through the next steps.',
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your OctetPay needs and next steps.',
    firstName: '{{firstName}}',
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}',
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our specialist team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'View Homepage',
      href: '/'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful trends, insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };

      // Map form data to Dynamics field format
      const dynamicsPayload = {
        ot_topic: 'OctetPay application',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_industry: (data.industry === 'Other' ? (data.customIndustry as string) || '' : (data.industry as string) || ''),
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Trust details (if applicable)
        pb_trusttype: (data.trusteeType as string) || '',
        pb_trusteename: (data.trusteeName as string) || '',
        pb_trustabnacn: (data.trustAbnAcn as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        // UTM parameters
        ...utmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true,
  onStepChange: (currentStep, totalSteps) => {
    console.info(`OctetPay Form - Step ${currentStep} of ${totalSteps}`);
  },
};
